// File: frontend/src/pages/Subscribe/Subscribe.js
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNewsletter } from '../../hooks/useNewsletter';
import { validateEmail } from '../../utils/validation';
import FormGroup from '../../components/form/FormGroup/FormGroup';
import Input from '../../components/form/Input/Input';
import Button from '../../components/Button/Button';
import Alert from '../../components/Alert/Alert';
import { FiMail, FiCheck } from 'react-icons/fi';
import styles from './Subscribe.module.css';

const Subscribe = () => {
  const { t } = useLanguage();
  const { subscribe, isLoading, error, success, clearError } = useNewsletter();
  
  const [formData, setFormData] = useState({
    emailAddress: '',
    firstName: '',
    lastName: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (field) => (value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation errors when user types
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.emailAddress.trim()) {
      errors.emailAddress = t('newsletter.errors.email_required');
    } else if (!validateEmail(formData.emailAddress.trim())) {
      errors.emailAddress = t('newsletter.errors.invalid_email');
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await subscribe(formData);
    } catch (err) {
      // Error handling is done in the hook
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.subscribeContainer}>
        <div className={styles.heroSection}>
          <h1 className={styles.title}>{t('newsletter.subscribe.title', 'Subscribe to Our Newsletter')}</h1>
          <div className={styles.titleUnderline}></div>
          <p className={styles.subtitle}>
            {t('newsletter.subscribe.subtitle', 'Stay updated with the latest news, product updates, and insights from Allegory.')}
          </p>
        </div>

        <div className={styles.formSection}>
          {error && (
            <Alert 
              type="error" 
              titleKey="newsletter.error.title"
              messageKey={`newsletter.errors.${error}`}
              onClose={clearError}
            />
          )}
          
          {success ? (
            <div className={styles.successContainer}>
              <div className={styles.successIcon}>
                <FiCheck size={40} />
              </div>
              <h2 className={styles.successTitle}>
                {t('newsletter.success.title', 'Thank You for Subscribing!')}
              </h2>
              <p className={styles.successMessage}>
                {t('newsletter.success.message', 'You have successfully subscribed to our newsletter. Please check your email for a confirmation message.')}
              </p>
              <div className={styles.benefitsList}>
                <h3>{t('newsletter.benefits.title', 'What to expect:')}</h3>
                <ul>
                  <li>{t('newsletter.benefits.updates', 'Product updates and new features')}</li>
                  <li>{t('newsletter.benefits.insights', 'Industry insights and best practices')}</li>
                  <li>{t('newsletter.benefits.content', 'Exclusive content and early access opportunities')}</li>
                  <li>{t('newsletter.benefits.tips', 'Tips and tutorials to make the most of Allegory')}</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className={styles.formContainer}>
              <div className={styles.formIntro}>
                <FiMail className={styles.formIcon} />
                <p className={styles.formDescription}>
                  {t('newsletter.subscribe.description', 'Join our community and receive the latest updates, news, and exclusive content directly to your inbox. We respect your privacy and will never share your information.')}
                </p>
              </div>
              
              <form onSubmit={handleSubmit} className={styles.form}>
                <FormGroup>
                  <Input
                    id="newsletter-email"
                    type="email"
                    labelKey="your_email_address"
                    value={formData.emailAddress}
                    onChange={handleChange('emailAddress')}
                    required
                    placeholderKey="auth.signIn.emailPlaceholder"
                    error={!!validationErrors.emailAddress}
                    errorKey={validationErrors.emailAddress}
                  />
                </FormGroup>

                <FormGroup>
                  <Input
                    id="newsletter-firstname"
                    type="text"
                    labelKey="auth.signUp.firstNameLabel"
                    value={formData.firstName}
                    onChange={handleChange('firstName')}
                    placeholderKey="auth.signUp.firstNamePlaceholder"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Input
                    id="newsletter-lastname"
                    type="text"
                    labelKey="auth.signUp.lastNameLabel"
                    value={formData.lastName}
                    onChange={handleChange('lastName')}
                    placeholderKey="auth.signUp.lastNamePlaceholder"
                  />
                </FormGroup>

                <div className={styles.privacyNote}>
                  <p>
                    {t('newsletter.privacy_note', 'By subscribing, you agree to our ')}
                    <a href="/privacy-policy" className={styles.inlineLink}>
                      {t('newsletter.privacy_policy', 'Privacy Policy')}
                    </a>
                    {t('newsletter.and_text', ' and ')}
                    <a href="/terms-of-service" className={styles.inlineLink}>
                      {t('newsletter.terms', 'Terms of Service')}
                    </a>.
                  </p>
                </div>

                <Button 
                  type="submit" 
                  variant="primary" 
                  size="medium" 
                  isLoading={isLoading}
                  showArrow
                  className={styles.submitButton}
                >
                  {t('subscribe', 'Subscribe')}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscribe;